import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import StatusIndicator from "components/ui/StatusIndicator";

export default function CourseCard({ course, onBook, onPreview, volunteerStatus }) {
  const isEligible = course?.requiredStatus?.includes(volunteerStatus);
  const isFull = course?.enrolled >= course?.capacity;
  const spotsLeft = course?.capacity - course?.enrolled;

  const statusColors = {
    available: "text-emerald-600 bg-emerald-50 border-emerald-200",
    full: "text-red-600 bg-red-50 border-red-200",
    upcoming: "text-blue-600 bg-blue-50 border-blue-200",
  };

  const courseStatus = isFull ? "full" : course?.status;

  return (
    <div
      className={`bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl shadow-[var(--shadow-sm)] flex flex-col transition-all duration-200 hover:shadow-[var(--shadow-md)] ${!isEligible ? "opacity-60" : ""}`}
    >
      {/* Header */}
      <div className="p-4 md:p-5 border-b border-[var(--color-border)]">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[var(--color-primary)] flex items-center justify-center flex-shrink-0">
              <Icon name={course?.icon} size={18} color="#fff" />
            </div>
            <h3 className="font-heading font-semibold text-sm md:text-base text-[var(--color-foreground)] line-clamp-2 leading-tight">
              {course?.title}
            </h3>
          </div>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${statusColors?.[courseStatus]}`}>
            {courseStatus === "full" ? "Full" : courseStatus === "upcoming" ? "Upcoming" : "Available"}
          </span>
        </div>
        <p className="text-xs md:text-sm text-[var(--color-muted-foreground)] line-clamp-2 leading-relaxed">
          {course?.description}
        </p>
      </div>
      {/* Details */}
      <div className="p-4 md:p-5 flex-1 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-[var(--color-muted-foreground)]">
            <Icon name="Clock" size={13} color="currentColor" />
            <span>{course?.duration}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[var(--color-muted-foreground)]">
            <Icon name="Users" size={13} color="currentColor" />
            <span>{spotsLeft > 0 ? `${spotsLeft} spots left` : "No spots"}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[var(--color-muted-foreground)]">
            <Icon name="Calendar" size={13} color="currentColor" />
            <span>{course?.nextSession}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[var(--color-muted-foreground)]">
            <Icon name="User" size={13} color="currentColor" />
            <span className="truncate">{course?.instructor}</span>
          </div>
        </div>

        {/* Category */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-muted)] text-[var(--color-muted-foreground)] font-medium">
            {course?.category}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-muted)] text-[var(--color-muted-foreground)] font-medium">
            {course?.format}
          </span>
        </div>

        {/* Status requirement */}
        <div className="flex items-center gap-1.5">
          <Icon name="Shield" size={12} color="var(--color-muted-foreground)" />
          <span className="text-xs text-[var(--color-muted-foreground)]">Required:</span>
          <div className="flex gap-1 flex-wrap">
            {course?.requiredStatus?.map((s) => (
              <StatusIndicator key={s} status={s} size="sm" showTooltip={false} />
            ))}
          </div>
        </div>

        {!isEligible && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
            <Icon name="Lock" size={12} color="currentColor" />
            <span>Requires status upgrade to enroll</span>
          </div>
        )}

        {/* Capacity bar */}
        <div>
          <div className="flex justify-between text-xs text-[var(--color-muted-foreground)] mb-1">
            <span>Enrollment</span>
            <span>{course?.enrolled}/{course?.capacity}</span>
          </div>
          <div className="progress-bar-track">
            <div
              className={`progress-bar-fill ${isFull ? "bg-red-500" : "bg-[var(--color-primary)]"}`}
              style={{ width: `${(course?.enrolled / course?.capacity) * 100}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto pt-1">
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            iconSize={14}
            onClick={() => onPreview(course)}
            className="flex-1"
          >
            Preview
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="BookOpen"
            iconPosition="left"
            iconSize={14}
            disabled={!isEligible || isFull}
            onClick={() => onBook(course)}
            className="flex-1"
          >
            {isFull ? "Full" : "Book Course"}
          </Button>
        </div>
      </div>
    </div>
  );
}